import { useEffect, useState } from 'react';
import {
    Divider,
    Typography,
    Switch,
    TextField,
    MenuItem,
    Select,
    FormControl,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    InputLabel,
    Grid,
} from '@mui/material';
import {
    MailOutline as MailIcon,
    WhatsApp as WhatsAppIcon,
    ErrorOutline as ErrorIcon,
    FilterList as FilterListIcon,
} from '@mui/icons-material';

import {
    GeneralContainer,
    FilterSidebar,
    FilterSection,
    FilterSectionTitle,
    ToggleRow,
    ToggleLabel,
    StatusButtonGroup,
    StatusButton,
    SearchButton,
    ClearButton,
    RightPanel,
    ContainerCards,
    OverviewCard,
    OverviewValue,
    OverviewLabel,
    NotDeliveredCard,
    NotDeliveredRow,
    NotDeliveredLabel,
    NotDeliveredBarWrapper,
    NotDeliveredBar,
    NotDeliveredValue,
    TableArea,
    TableHeader,
    TableTitle,
    TableCount,
    TableScrollArea,
} from './Logs.styled';
import CustomTableSortLabel from '../../components/custom-table-sort-label/CustomTableSortLabel';
import LogsListRow from './LogsListRow';
import CustomTablePagination from '../../components/custom-table-pagination/CustomTablePagination';
import { DatePicker, LocalizationProvider, ptBR } from '@mui/x-date-pickers';
import moment from 'moment';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useListForLogs } from '../../hooks/services/useListForLogs';
import { ILogsSummary } from '../../interfaces/ILogsResult';
import { useToastrVanila } from '../../hooks/util/useToastrVanila';
import LogsCardsSkeleton from './skeleton/LogsCardsSkeleton';
import LogsTableSkeleton from './skeleton/LogsTableSkeleton';

export interface HeaderLogs {
    field: string;
    label: string;
    sticky?: boolean;
    width?: number;
    render?: (row: any) => React.ReactNode;
}

interface LogsProps {
    title: string;
}

const Logs: React.FC<LogsProps> = ({ title }) => {
    window.document.title = title;
    const [logs, setLogs] = useState<any[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [summary, setSummary] = useState<ILogsSummary>({
        totalEmails: 0,
        totalWhatsapp: 0,
        errorEmails: 0,
        errorWhatsapp: 0,
    });
    const [loading, setLoading] = useState(false);

    // --- Estados de paginação e ordenação ---
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('desc');
    const [orderField, setOrderField] = useState<string>('sentAt');

    // --- Estados de filtros ---
    const [initialDate, setInitialDate] = useState<moment.Moment | null>(
        moment().subtract(5, 'days').startOf('day'),
    );
    const [finalDate, setFinalDate] = useState<moment.Moment | null>(moment().endOf('day'));
    const [emailEnabled, setEmailEnabled] = useState(true);
    const [whatsappEnabled, setWhatsappEnabled] = useState(true);
    const [statusFilter, setStatusFilter] = useState<'ALL' | 'SUCCESS' | 'ERROR'>('ALL');
    const [recipientFilter, setRecipientFilter] = useState('');
    const [subjectFilter, setSubjectFilter] = useState('');
    const [templateNameFilter, setTemplateNameFilter] = useState('');

    const [templateOptions, setTemplateOptions] = useState<{ ids: number[]; name: string }[]>([]);

    const request = useListForLogs();
    const toastr = useToastrVanila();

    const headers: HeaderLogs[] = [
        { field: 'sentAt', label: 'Hora', render: (row) => moment(row.sentAt).format('DD/MM/YYYY HH:mm') },
        { field: 'recipient', label: 'Destinatário', render: (row) => row.recipient },
        { field: 'subject', label: 'Assunto', render: (row) => row.subject },
        { field: 'templateName', label: 'Template', render: (row) => row.templateName },
        {
            field: 'status', label: 'Status', render: (row) => row.status === 'SUCCESS'
                ? <Typography sx={{ color: '#2e7d32', fontWeight: 500, fontSize: 12, letterSpacing: 0.5 }}>Sucesso</Typography>
                : <Typography sx={{ color: '#c62828', fontWeight: 500, fontSize: 12, letterSpacing: 0.5 }}>Erro no envio</Typography>
        },
        {
            field: 'channel', label: 'Canal', render: (row) => row.channel === 'email'
                ? <><MailIcon sx={{ fontSize: 12, color: '#1976d2', display: 'inline-block', verticalAlign: 'middle' }} /> {row.channel}</>
                : <><WhatsAppIcon sx={{ fontSize: 12, color: '#25D366', display: 'inline-block', verticalAlign: 'middle' }} /> {row.channel}</>
        }
    ];

    const getChannels = (): ('email' | 'whatsapp')[] => {
        const channels: ('email' | 'whatsapp')[] = [];
        if (emailEnabled) channels.push('email');
        if (whatsappEnabled) channels.push('whatsapp');
        return channels;
    };

    const list = async (
        rowsPerPage: number = perPage,
        page: number = currentPage,
        field: string = orderField,
        direction: 'asc' | 'desc' = orderDirection,
    ) => {
        setLoading(true);

        const filters = {
            initialDate: initialDate?.format('YYYY-MM-DD') || '',
            finalDate: finalDate?.format('YYYY-MM-DD') || '',
            channels: getChannels(),
            status: statusFilter,
            recipient: recipientFilter || undefined,
            subject: subjectFilter || undefined,
            templateName: templateNameFilter || undefined,
        };

        try {
            const result = await request.listForLogs(
                rowsPerPage,
                page + 1,
                field,
                direction,
                filters,
            );
            setLogs(result.data);
            setTotalRows(result.totalRows);
            setSummary(result.summary);
        } catch (error: any) {
            toastr.error('Erro', error.message || 'Erro ao carregar logs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        request.listTemplates().then(setTemplateOptions);
        list();
    }, []);

    const handleSearch = () => {
        setCurrentPage(0);
        list(0);
    };

    const handleClearFilters = () => {
        setEmailEnabled(true);
        setWhatsappEnabled(true);
        setStatusFilter('ALL');
        setRecipientFilter('');
        setSubjectFilter('');
        setTemplateNameFilter('');
        setInitialDate(moment().subtract(5, 'days').startOf('day'));
        setFinalDate(moment().endOf('day'));
        setCurrentPage(0);
        // Busca com filtros limpos no próximo tick
        setTimeout(() => list(0), 0);
    };

    // --- Cálculo de porcentagem para barras de "não entregues" ---
    const maxErrors = Math.max(summary.errorEmails, summary.errorWhatsapp, 1);
    const emailErrorPercent = `${(summary.errorEmails / maxErrors) * 100}%`;
    const whatsappErrorPercent = `${(summary.errorWhatsapp / maxErrors) * 100}%`;

    return (
        <GeneralContainer>
            {/* ═══════════ ESQUERDA: Filtros ═══════════ */}
            <FilterSidebar>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, fontSize: 13 }}>
                    <FilterListIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 1 }} />
                    Filtros
                </Typography>
                <Divider />

                {/* Canal */}
                <FilterSection>
                    <FilterSectionTitle>Canal</FilterSectionTitle>
                    <ToggleRow>
                        <ToggleLabel>
                            <MailIcon sx={{ fontSize: 16, opacity: 0.45, color: '#1976d2' }} />
                            E-mail
                        </ToggleLabel>
                        <Switch
                            checked={emailEnabled}
                            onChange={(e) => setEmailEnabled(e.target.checked)}
                            size="small"
                        />
                    </ToggleRow>
                    <ToggleRow>
                        <ToggleLabel>
                            <WhatsAppIcon sx={{ fontSize: 16, opacity: 0.45, color: '#25D366' }} />
                            WhatsApp
                        </ToggleLabel>
                        <Switch
                            checked={whatsappEnabled}
                            onChange={(e) => setWhatsappEnabled(e.target.checked)}
                            size="small"
                        />
                    </ToggleRow>
                </FilterSection>

                <Divider />

                {/* Período */}
                <FilterSection>
                    <Grid container md={12} sm={12} xs={12}>
                        <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 1, fontWeight: 500 }}>
                            Período:
                        </Typography>
                        <Grid container spacing={1} gap={1}>
                            <Grid item md={12} sm={12} xs={12}>
                                <LocalizationProvider
                                    dateAdapter={AdapterMoment}
                                    localeText={ptBR.components.MuiLocalizationProvider.defaultProps.localeText}
                                >
                                    <DatePicker
                                        value={initialDate}
                                        label="Data Inicial"
                                        onChange={e => {
                                            if (moment(e).isValid()) {
                                                setCurrentPage(0);
                                                setInitialDate(e);
                                            }
                                        }}
                                        renderInput={params => <TextField {...params} size="small" fullWidth />}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item md={12} sm={12} xs={12}>
                                <LocalizationProvider
                                    dateAdapter={AdapterMoment}
                                    localeText={ptBR.components.MuiLocalizationProvider.defaultProps.localeText}
                                >
                                    <DatePicker
                                        value={finalDate}
                                        label="Data Final"
                                        onChange={e => {
                                            if (moment(e).isValid()) {
                                                setCurrentPage(0);
                                                setFinalDate(e);
                                            }
                                        }}
                                        renderInput={params => <TextField {...params} size="small" fullWidth />}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </Grid>
                    </Grid>
                </FilterSection>

                <Divider />

                {/* Status */}
                <FilterSection sx={{ mt: 1 }}>
                    <FilterSectionTitle>Status</FilterSectionTitle>
                    <StatusButtonGroup>
                        <StatusButton
                            size="small"
                            selected={statusFilter === 'ALL'}
                            statusType="all"
                            onClick={() => setStatusFilter('ALL')}
                        >
                            Todos
                        </StatusButton>
                        <StatusButton
                            size="small"
                            selected={statusFilter === 'SUCCESS'}
                            statusType="success"
                            onClick={() => setStatusFilter('SUCCESS')}
                        >
                            Sucesso
                        </StatusButton>
                        <StatusButton
                            size="small"
                            selected={statusFilter === 'ERROR'}
                            statusType="error"
                            onClick={() => setStatusFilter('ERROR')}
                        >
                            Erro
                        </StatusButton>
                    </StatusButtonGroup>
                </FilterSection>

                <Divider />

                {/* Buscar */}
                <FilterSection sx={{ mt: 1 }}>
                    <FilterSectionTitle>Buscar</FilterSectionTitle>
                    <TextField
                        placeholder="Destinatário..."
                        size="small"
                        fullWidth
                        sx={{ mb: 1 }}
                        value={recipientFilter}
                        onChange={(e) => setRecipientFilter(e.target.value)}
                    />
                    <TextField
                        placeholder="Assunto..."
                        size="small"
                        fullWidth
                        sx={{ mb: 1 }}
                        value={subjectFilter}
                        onChange={(e) => setSubjectFilter(e.target.value)}
                    />
                    <FormControl size="small" fullWidth>
                        <InputLabel>Template</InputLabel>
                        <Select
                            label="Template"
                            value={templateNameFilter}
                            onChange={(e) => setTemplateNameFilter(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {templateOptions.map((t) => (
                                <MenuItem key={t.name} value={t.name}>
                                    {t.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </FilterSection>

                <SearchButton variant="contained" onClick={handleSearch}>
                    Buscar
                </SearchButton>
                <ClearButton onClick={handleClearFilters}>
                    Limpar filtros
                </ClearButton>
            </FilterSidebar>

            {/* ═══════════ DIREITA: Cards + Tabela ═══════════ */}
            <RightPanel>
                {/* ── Cards de overview ── */}
                {loading ? <LogsCardsSkeleton /> : (
                    <ContainerCards>
                        <OverviewCard>
                            <div>
                                <OverviewLabel>
                                    <MailIcon sx={{ fontSize: 14, opacity: 0.5, color: '#1976d2' }} />
                                    E-mail
                                </OverviewLabel>
                                <OverviewValue>{summary.totalEmails}</OverviewValue>
                            </div>
                        </OverviewCard>

                        <OverviewCard>
                            <div>
                                <OverviewLabel>
                                    <WhatsAppIcon sx={{ fontSize: 14, opacity: 0.5, color: '#25D366' }} />
                                    WhatsApp
                                </OverviewLabel>
                                <OverviewValue>{summary.totalWhatsapp}</OverviewValue>
                            </div>
                        </OverviewCard>

                        <NotDeliveredCard>
                            <OverviewLabel sx={{ mb: '6px' }}>
                                <ErrorIcon sx={{ fontSize: 14, color: '#c62828' }} />
                                Não entregues
                            </OverviewLabel>
                            <NotDeliveredRow>
                                <NotDeliveredLabel>
                                    <MailIcon sx={{ fontSize: 12 }} />
                                    E-mail
                                </NotDeliveredLabel>
                                <NotDeliveredBarWrapper>
                                    <NotDeliveredBar width={emailErrorPercent} />
                                </NotDeliveredBarWrapper>
                                <NotDeliveredValue>{summary.errorEmails}</NotDeliveredValue>
                            </NotDeliveredRow>
                            <NotDeliveredRow>
                                <NotDeliveredLabel>
                                    <WhatsAppIcon sx={{ fontSize: 12 }} />
                                    WhatsApp
                                </NotDeliveredLabel>
                                <NotDeliveredBarWrapper>
                                    <NotDeliveredBar width={whatsappErrorPercent} />
                                </NotDeliveredBarWrapper>
                                <NotDeliveredValue>{summary.errorWhatsapp}</NotDeliveredValue>
                            </NotDeliveredRow>
                        </NotDeliveredCard>
                    </ContainerCards>)}

                {/* ── Tabela ── */}
                <TableArea>
                    <TableHeader>
                        <div style={{ display: 'flex', alignItems: 'baseline' }}>
                            <TableTitle>Registro de atividade</TableTitle>
                            <TableCount>{totalRows} resultados</TableCount>
                        </div>
                    </TableHeader>
                    <TableScrollArea>
                        <TableContainer>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow  >
                                        {headers.map((head) => (
                                            <CustomTableSortLabel
                                                key={head.field}
                                                label={head.label}
                                                direction={orderDirection}
                                                field={head.field}
                                                perPage={perPage}
                                                currentPage={currentPage}
                                                orderField={orderField}
                                                setDirection={setOrderDirection}
                                                setField={setOrderField}
                                                handleRequest={list}
                                            />
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? <LogsTableSkeleton headers={headers} rows={perPage} /> :
                                        logs.map((row) => (
                                            <LogsListRow key={row.id} headers={headers} row={row} />
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </TableScrollArea>
                    <CustomTablePagination
                        totalRows={totalRows}
                        currentPage={currentPage}
                        perPage={perPage}
                        handleRequest={list}
                        setPerPage={setPerPage}
                        setCurrentPage={setCurrentPage}
                        orderField={orderField}
                        orderDirection={orderDirection}
                    />
                </TableArea>
            </RightPanel>
        </GeneralContainer>
    );
};

export default Logs;

